import IOrder from "../models/IOrder";
import OrderStatesEnum from "../models/OrderStatesEnum";

// Statuses that should be BLOCKED from daily routes entirely
export const BLOCKED_ORDER_STATUSES: OrderStatesEnum[] = [
    OrderStatesEnum.PRELIMINARY,
    OrderStatesEnum.SHIPPED,
    OrderStatesEnum.DELIVERED,
    OrderStatesEnum.PICKED_UP,
];

// Target "ready" statuses for daily routes
export const READY_STATUSES: OrderStatesEnum[] = [OrderStatesEnum.READY_FOR_DELIVERY, OrderStatesEnum.READY_FOR_PICKUP];

export interface IOrderStatusValidation {
    isBlocked: boolean;
    needsStatusChange: boolean;
    blockReason?: string;
    currentStatus?: OrderStatesEnum;
}

/**
 * Validates if an order can be added to a daily route
 */
const validateOrderForDailyRoute = (order: IOrder): IOrderStatusValidation => {
    const orderStateId = order.order_state_id;

    // Check if blocked
    if (orderStateId && BLOCKED_ORDER_STATUSES.includes(orderStateId)) {
        return {
            isBlocked: true,
            needsStatusChange: false,
            blockReason: getBlockedReason(orderStateId),
            currentStatus: orderStateId,
        };
    }

    // Check if needs status change (not in READY status)
    if (orderStateId && !READY_STATUSES.includes(orderStateId)) {
        return {
            isBlocked: false,
            needsStatusChange: true,
            currentStatus: orderStateId,
        };
    }

    return { isBlocked: false, needsStatusChange: false };
};

/**
 * Get human-readable reason why order is blocked (translation key)
 */
const getBlockedReason = (status: OrderStatesEnum): string => {
    switch (status) {
        case OrderStatesEnum.PRELIMINARY:
            return "order_is_preliminary";
        case OrderStatesEnum.SHIPPED:
            return "order_already_shipped";
        case OrderStatesEnum.DELIVERED:
            return "order_already_delivered";
        case OrderStatesEnum.PICKED_UP:
            return "order_already_picked_up";
        default:
            return "order_status_invalid_for_route";
    }
};

/**
 * Filter orders to remove blocked ones
 */
const filterBlockedOrders = (orders: IOrder[]): { validOrders: IOrder[]; blockedOrders: { order: IOrder; reason: string }[] } => {
    const validOrders: IOrder[] = [];
    const blockedOrders: { order: IOrder; reason: string }[] = [];

    for (const order of orders) {
        const validation = validateOrderForDailyRoute(order);
        if (validation.isBlocked) {
            blockedOrders.push({ order, reason: validation.blockReason! });
        } else {
            validOrders.push(order);
        }
    }

    return { validOrders, blockedOrders };
};

/**
 * Get orders that need status change warning (not in READY_FOR_DELIVERY or READY_FOR_PICKUP)
 */
const getOrdersNeedingStatusChange = (orders: IOrder[]): IOrder[] => {
    return orders.filter((order) => {
        const validation = validateOrderForDailyRoute(order);
        return validation.needsStatusChange;
    });
};

const DailyRouteService = {
    validateOrderForDailyRoute,
    getBlockedReason,
    filterBlockedOrders,
    getOrdersNeedingStatusChange,
    BLOCKED_ORDER_STATUSES,
    READY_STATUSES,
};

export default DailyRouteService;
